import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getBankAccountsList } from '../../services/bank-account-services';
import { getOpenInvoices, getScheduledPayments } from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { shouldDisablePayNow } from '../../utils/helper';
import { useContracts } from '../useContracts';
import { useStaleOnResume } from '../useStaleOnResume';

import type { ContractApiResponse } from '../../services/contract-services';
import type { OpenInvoiceApiResponse } from '../../services/payment-services';

export type BankAccountOption = { id: string; name: string; country: string };

export interface PaymentRowLike {
    contractId: string;
    dbContractId: string;
}

export interface ScheduledPayment {
    Id?: string;
    ContractId?: string | number;
    contractId?: string | number;
    AspireContractId?: string;
    aspireContractId?: string;
    Amount?: number | string;
    amount?: number | string;
    Status?: number | string;
    status?: number | string;
    Platform?: number;
}

/**
 * Owns all read-only contract/invoice/bank-account data used across the Make Payment page:
 * fetching, the active-contracts filter, and lookup/derivation helpers built on top of it.
 */
export const useContractPaymentData = () => {
    const dispatch = useDispatch();
    const userDetails = useSelector((state: RootState) => state.user.userDetails);
    const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
    const { contracts: contractsFromStore, fetchContracts } = useContracts();
    const userContracts = contractsFromStore;

    const [activeContracts, setActiveContracts] = useState<ContractApiResponse[]>([]);
    const [bankAccountOptions, setBankAccountOptions] = useState<BankAccountOption[]>([]);
    const [openInvoices, setOpenInvoices] = useState<OpenInvoiceApiResponse[]>([]);
    const [scheduledPayments, setScheduledPayments] = useState<ScheduledPayment[]>([]);
    const [pendingScheduleAmount, setPendingScheduleAmount] = useState<
        Array<{ contractId: string; totalPendingScheduleAmount: number }>
    >([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const initialDataFetch = useRef(false);

    useEffect(() => {
        if (contractsFromStore && Array.isArray(contractsFromStore)) {
            const filtered = contractsFromStore?.filter(
                contractItem => contractItem?.Status === 1 || !contractItem?.Status,
            );
            setActiveContracts(filtered);
        }
    }, [contractsFromStore]);

    const userContractsCountryCombinations = useMemo(
        () =>
            userContracts?.length > 0 ? userContracts?.map(item => ({ country: item.Country, state: item.State })) : [],
        [userContracts],
    );

    const getBankAccounts = useCallback(
        async (showLoaderOverlay: boolean = true) => {
            try {
                if (showLoaderOverlay) dispatch(showLoader());
                const response = await getBankAccountsList(false);
                if (response?.statusCode === 200 && Array.isArray(response.data)) {
                    const options = response?.data
                        ?.filter(bankAccount => bankAccount?.IsActive && bankAccount?.AspireIsActive !== false)
                        .map(bankAccount => ({
                            id: bankAccount.Id,
                            name: `${bankAccount.BankName} (****${bankAccount.AccountNumber.slice(-4)} - ${bankAccount.AccountType})${bankAccount.NickName ? ` - ${bankAccount.NickName}` : 'N/A'}`,
                            country: bankAccount.Country || '',
                        }));
                    setBankAccountOptions(options);
                }
            } catch (error) {
                console.error('Error fetching bank accounts:', error);
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: 'Failed to Load Bank Accounts',
                        severity: 'error',
                    }),
                );
            } finally {
                if (showLoaderOverlay) dispatch(hideLoader());
            }
        },
        [dispatch],
    );

    const getOpenInvoicesData = useCallback(
        async (showLoaderOverlay: boolean = true) => {
            try {
                if (showLoaderOverlay) dispatch(showLoader());
                const response = await getOpenInvoices(false);
                if (response.statusCode === 200 && Array.isArray(response.data)) {
                    setOpenInvoices(response.data);
                    // Extract and store pending schedule amount from response
                    if (Array.isArray(response.pendingScheduleAmount)) {
                        setPendingScheduleAmount(response.pendingScheduleAmount);
                    }
                }
            } catch (error) {
                console.error('Error fetching open invoices:', error);
            } finally {
                if (showLoaderOverlay) dispatch(hideLoader());
            }
        },
        [dispatch],
    );

    const getScheduledPaymentsData = useCallback(async () => {
        try {
            const response = await getScheduledPayments(false);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setScheduledPayments(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching scheduled payments:', error);
        }
    }, []);

    const getUserContractsData = useCallback(async () => {
        try {
            await fetchContracts(false);
        } catch (error) {
            console.error('Error fetching user contracts:', error);
        }
    }, [fetchContracts]);

    useEffect(() => {
        if (initialDataFetch.current) return;
        initialDataFetch.current = true;

        const initializeData = async () => {
            setIsLoadingData(true);
            try {
                await Promise.all([
                    getUserContractsData(),
                    getBankAccounts(),
                    getOpenInvoicesData(),
                    getScheduledPaymentsData(),
                ]);
            } finally {
                setIsLoadingData(false);
            }
        };
        initializeData();
    }, [getBankAccounts, getOpenInvoicesData, getUserContractsData, getScheduledPaymentsData]);

    // Re-fetch bank accounts, open invoices, and scheduled payments when the tab regains focus
    // after sitting hidden long enough to be considered stale (contracts refresh on their own
    // via useContracts). Background refresh — no loader overlay.
    const refetchOnResume = useCallback(async () => {
        await Promise.all([getBankAccounts(false), getOpenInvoicesData(false), getScheduledPaymentsData()]);
    }, [getBankAccounts, getOpenInvoicesData, getScheduledPaymentsData]);
    useStaleOnResume(refetchOnResume, isLoggedIn);

    /**
     * Get the total amount and contract info for all unscheduled invoices with a given contract ID
     * When multiple invoices exist for the same contract, sum all their unscheduled amounts
     * Invoices with is_scheduled === true are excluded from the total
     * @param contractId - The Aspire contract ID
     * @param allowOutstandingBalanceFallback - If false (card payments), don't fallback to outstanding balance
     * @returns An object with the total amount, database contract ID, or undefined if no unscheduled invoice found
     */
    const getTotalAmountForContract = useCallback(
        (contractId: string, allowOutstandingBalanceFallback: boolean = true) => {
            const searchId = String(contractId).trim().toUpperCase();

            // Always prioritize summing open invoices for accurate due amount calculation
            if (openInvoices && openInvoices.length > 0) {
                // Filter invoices for this contract - check both capital and lowercase field names
                // Match by AspireContractId or database ContractId (case-insensitive)
                // Include ALL invoices (scheduled and unscheduled) - remaining due calculation handles scheduled payments
                const contractInvoices = openInvoices.filter((inv: OpenInvoiceApiResponse) => {
                    const aspireId = String(inv?.AspireContractId || inv?.aspireContractId || '')
                        .trim()
                        .toUpperCase();
                    const dbId = String(inv?.contractId || inv?.ContractId || '')
                        .trim()
                        .toUpperCase();
                    return aspireId === searchId || dbId === searchId;
                });

                if (contractInvoices.length > 0) {
                    // Sum all amounts for this contract ID
                    const totalAmount = contractInvoices.reduce((sum: number, invoice: OpenInvoiceApiResponse) => {
                        const amountStr = invoice?.Amount || invoice?.amount || '0';
                        const amount = parseFloat(String(amountStr));
                        return sum + (isNaN(amount) ? 0 : amount);
                    }, 0);

                    // Get the database contract ID from the first invoice (should be same for all)
                    const firstInvoice = contractInvoices[0];
                    const dbContractId = firstInvoice?.ContractId || firstInvoice?.contractId || '';

                    return {
                        totalAmount: totalAmount.toFixed(2),
                        dbContractId,
                        invoiceCount: contractInvoices.length,
                    };
                }
            }

            // Fallback to outstanding balance only if no open invoices exist AND fallback is allowed
            // Card payments should show $0.00 due when no invoices, but still allow "Other Amount" entry
            if (!allowOutstandingBalanceFallback) {
                const contract = activeContracts.find((c: ContractApiResponse) => {
                    const aspireId = String(c?.AspireId || '')
                        .trim()
                        .toUpperCase();
                    const dbId = String(c?.Id || '')
                        .trim()
                        .toUpperCase();
                    return aspireId === searchId || dbId === searchId;
                });
                if (contract) {
                    const dbContractId = contract?.Id?.toString() || contract?.AspireId || '';
                    return {
                        totalAmount: '0.00',
                        dbContractId,
                        invoiceCount: 0,
                    };
                }
                return undefined;
            }

            const contract = activeContracts.find((c: ContractApiResponse) => {
                const aspireId = String(c?.AspireId || '')
                    .trim()
                    .toUpperCase();
                const dbId = String(c?.Id || '')
                    .trim()
                    .toUpperCase();
                return aspireId === searchId || dbId === searchId;
            });

            if (contract) {
                const outstandingBalance = parseFloat(String(contract.OutstandingBalance || '0'));
                if (!isNaN(outstandingBalance) && outstandingBalance > 0) {
                    const dbContractId = contract?.Id?.toString() || contract?.AspireId || '';
                    return {
                        totalAmount: outstandingBalance.toFixed(2),
                        dbContractId,
                        invoiceCount: 1,
                    };
                }
            }

            return undefined;
        },
        [openInvoices, activeContracts],
    );

    /**
     * Get the total pending scheduled amount for a given contract ID
     * @param contractId - The Aspire contract ID
     * @returns The total pending scheduled amount or '0.00' if not found
     */
    const getScheduledAmountForContract = useCallback(
        (contractId: string): string => {
            // Normalize the contract ID for comparison
            const normalizedSearchId = String(contractId).trim().toUpperCase();

            // First, try to get total from scheduled payments (includes all non-cancelled payments)
            if (scheduledPayments && scheduledPayments.length > 0) {
                const totalFromScheduled = scheduledPayments
                    .filter((payment: ScheduledPayment) => {
                        // Exclude cancelled (2) and failed (3) payments — failed payments never
                        // collected money, so they must not reduce the remaining amount due.
                        const status = payment?.Status ?? payment?.status;
                        if (Number(status) === 2 || Number(status) === 3) return false;

                        // Match contract ID
                        const paymentContractId = String(payment?.ContractId ?? payment?.contractId ?? '')
                            .trim()
                            .toUpperCase();
                        return paymentContractId === normalizedSearchId;
                    })
                    .reduce((sum: number, payment: ScheduledPayment) => {
                        const amount = Number(payment?.Amount ?? payment?.amount ?? 0);
                        return sum + amount;
                    }, 0);

                if (totalFromScheduled > 0) {
                    return totalFromScheduled.toFixed(2);
                }
            }

            // Fallback to pendingScheduleAmount if scheduled payments data is not available
            if (!pendingScheduleAmount || pendingScheduleAmount.length === 0) {
                return '0.00';
            }

            const contractSchedule = pendingScheduleAmount.find(
                (item: { contractId: string; totalPendingScheduleAmount: number }) => {
                    const normalizedItemId = String(item.contractId).trim().toUpperCase();
                    return normalizedItemId === normalizedSearchId;
                },
            );

            if (!contractSchedule) {
                return '0.00';
            }

            const amount = contractSchedule.totalPendingScheduleAmount || 0;
            return parseFloat(String(amount)).toFixed(2);
        },
        [scheduledPayments, pendingScheduleAmount],
    );

    // Helper function to check if a contract can be selected
    // Business Rules by Account Type:
    // 1. Consumer Loan (I + IsLoan): Selectable with open invoices OR outstanding balance (ACH principal-only payments)
    // 2. Consumer Lease (I + !IsLoan): Selectable with open invoices OR outstanding balance (ACH principal-only payments)
    // 3. Commercial Loan (B + IsLoan): Selectable with open invoices OR outstanding balance (ACH principal-only payments)
    // 4. Commercial Lease (B + !IsLoan): Selectable with open invoices OR outstanding balance (ACH principal-only payments)
    const canSelectContract = useCallback(
        (contractId: string): boolean => {
            const contract = activeContracts.find(
                (c: ContractApiResponse) => c.AspireId === contractId || String(c.Id ?? '') === contractId,
            );
            if (!contract) return false;

            // Check if contract is disabled (e.g., autopay enabled, past due, etc.)
            if (shouldDisablePayNow(contractId, activeContracts)) return false;

            // Check for open invoices
            const contractData = getTotalAmountForContract(contractId);
            const invoiceAmount = parseFloat(contractData?.totalAmount ?? '0');
            const hasOpenInvoices = Number.isFinite(invoiceAmount) && invoiceAmount > 0;

            // Check for outstanding balance (enables ACH principal-only payments without invoices)
            const outstandingBalance = Number(contract.OutstandingBalance) || 0;
            const hasOutstandingBalance = outstandingBalance > 0;

            // Allow selection if either open invoices exist OR outstanding balance exists
            return hasOpenInvoices || hasOutstandingBalance;
        },
        [activeContracts, getTotalAmountForContract],
    );

    // Helper: Get contract object by AspireId or dbContractId
    const getContractById = useCallback(
        (contractId: string) => {
            if (contractId == null || String(contractId).trim() === '') return null;
            const key = String(contractId).trim();
            return activeContracts.find(c => c.AspireId === key || c.Id?.toString() === key) || null;
        },
        [activeContracts],
    );

    /**
     * Resolve the full contract for a payment row. Row keys can be AspireId or DB Id; invoice payloads
     * sometimes disagree with how the row was keyed — fall back to matching open invoices then re-resolve.
     */
    const resolveContractForPaymentRow = useCallback(
        (row: PaymentRowLike): ContractApiResponse | null => {
            const c1 = getContractById(String(row.contractId ?? '').trim());
            if (c1) return c1;
            const c2 = getContractById(String(row.dbContractId ?? '').trim());
            if (c2) return c2;

            const r1 = String(row.contractId ?? '').trim();
            const r2 = String(row.dbContractId ?? '').trim();
            const inv = openInvoices.find((i: OpenInvoiceApiResponse) => {
                const a = String(i?.AspireContractId ?? i?.aspireContractId ?? '').trim();
                const cid = String(i?.ContractId ?? i?.contractId ?? '').trim();
                if (!a && !cid) return false;
                return (r1 && (a === r1 || cid === r1)) || (r2 && (a === r2 || cid === r2));
            });
            if (!inv) return null;
            const invAspire = String(inv?.AspireContractId ?? inv?.aspireContractId ?? '').trim();
            const invCid = String(inv?.ContractId ?? inv?.contractId ?? '').trim();
            return getContractById(invAspire) || getContractById(invCid) || null;
        },
        [getContractById, openInvoices],
    );

    /**
     * Consumer loan: under/over payment UI. Contract-scoped only (matches canSelectContract and
     * helper.ts's canMakePaymentForContract) — userDetails.AccountType is an account-level field,
     * not per-contract, and gating on it here caused genuine consumer-loan rows to lose the
     * Full/Other-Amount choice whenever it didn't match the contract's own AccountType.
     */
    const isConsumerLoanContract = useCallback(
        (contract: ContractApiResponse | null | undefined) =>
            Boolean(contract && contract.IsLoan && userDetails?.AccountType === 'I' && contract.AccountType === 'I'),
        [userDetails],
    );

    const normalizeCountryForTax = (country: string | undefined): string => {
        const value = String(country || '')
            .trim()
            .toLowerCase();
        if (value === 'usa' || value === 'united states' || value === 'us') return 'US';
        if (value === 'canada' || value === 'ca') return 'CA';
        return (country || 'US').toUpperCase();
    };

    const getContractTaxAddress = useCallback(
        (contractId: string) => {
            const contract = activeContracts.find(
                (c: ContractApiResponse) => c.AspireId === contractId || c.Id?.toString() === contractId,
            );
            return {
                line1: String(contract?.Address1 || contract?.AddressLine1 || ''),
                city: String(contract?.City || ''),
                state: String(contract?.State || ''),
                postal_code: String(contract?.PostalCode || ''),
                country: normalizeCountryForTax(contract?.Country),
            };
        },
        [activeContracts],
    );

    /** Check if a contract has all open invoices scheduled (nothing left to pay) */
    const isContractFullyScheduled = useCallback(
        (contractId: string): boolean => {
            if (!openInvoices || openInvoices.length === 0) return false;

            const searchId = String(contractId).trim().toUpperCase();

            // Find all invoices for this contract (match by AspireContractId or database ContractId)
            const contractInvoices = openInvoices.filter((inv: OpenInvoiceApiResponse) => {
                const aspireId = String(inv?.AspireContractId || inv?.aspireContractId || '')
                    .trim()
                    .toUpperCase();
                const dbId = String(inv?.contractId || inv?.ContractId || '')
                    .trim()
                    .toUpperCase();
                return aspireId === searchId || dbId === searchId;
            });

            if (contractInvoices.length === 0) return false;

            // Calculate total invoice amount
            const totalInvoiceAmount = contractInvoices.reduce((sum: number, inv: OpenInvoiceApiResponse) => {
                const amount = parseFloat(String(inv?.Amount || inv?.amount || '0'));
                return sum + (isNaN(amount) ? 0 : amount);
            }, 0);

            // Calculate total card payment amount (Platform === 2, only Status 0=pending or 1=processed)
            const totalScheduledAmount = (scheduledPayments || [])
                .filter((payment: ScheduledPayment) => {
                    // Only include card payments (Platform === 2)
                    if (Number(payment?.Platform) !== 2) return false;
                    // Only include pending (0) or processed (1) payments, exclude cancelled (2) and failed (3)
                    const status = Number(payment?.Status);
                    if (status !== 0 && status !== 1) return false;

                    // Match by AspireContractId or database ContractId
                    const paymentAspireId = String(payment?.AspireContractId || payment?.aspireContractId || '')
                        .trim()
                        .toUpperCase();
                    const paymentDbId = String(payment?.ContractId || payment?.contractId || '')
                        .trim()
                        .toUpperCase();
                    return paymentAspireId === searchId || paymentDbId === searchId;
                })
                .reduce((sum: number, payment: ScheduledPayment) => {
                    const amount = Number(payment?.Amount || payment?.amount || 0);
                    return sum + amount;
                }, 0);

            // If scheduled card payment amount >= total invoice amount, all invoices are covered
            return totalScheduledAmount >= totalInvoiceAmount;
        },
        [openInvoices, scheduledPayments],
    );

    /** Check if any scheduled payment (any platform) is pending or processed for a contract */
    const hasActiveScheduledPayment = useCallback(
        (contractId: string): boolean => {
            if (!scheduledPayments || scheduledPayments.length === 0) return false;

            const searchId = String(contractId).trim().toUpperCase();

            return (scheduledPayments || []).some((payment: ScheduledPayment) => {
                // Only consider pending (0) or processed (1) payments
                const status = Number(payment?.Status);
                if (status !== 0 && status !== 1) return false;

                // Match by AspireContractId or database ContractId
                const paymentAspireId = String(payment?.AspireContractId || payment?.aspireContractId || '')
                    .trim()
                    .toUpperCase();
                const paymentDbId = String(payment?.ContractId || payment?.contractId || '')
                    .trim()
                    .toUpperCase();
                return paymentAspireId === searchId || paymentDbId === searchId;
            });
        },
        [scheduledPayments],
    );

    return {
        userDetails,
        activeContracts,
        bankAccountOptions,
        setBankAccountOptions,
        openInvoices,
        pendingScheduleAmount,
        isLoadingData,
        userContractsCountryCombinations,
        getBankAccounts,
        getOpenInvoicesData,
        getScheduledPaymentsData,
        getUserContractsData,
        getTotalAmountForContract,
        getScheduledAmountForContract,
        canSelectContract,
        getContractById,
        resolveContractForPaymentRow,
        isConsumerLoanContract,
        getContractTaxAddress,
        isContractFullyScheduled,
        hasActiveScheduledPayment,
    };
};
